import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, AlertTriangle, ShieldCheck, ClipboardCheck, CalendarDays, FolderLock, History, Inbox, Settings as SettingsIcon, LogOut, ChevronDown, Plus } from "lucide-react";
import { toast } from "sonner";
import { boardFetch } from "./api";
import { useAuth } from "./BoardAuth";
import { CompanyContext, Modal } from "./components";

const NAV = [
  { to: "/board", end: true, icon: LayoutDashboard, label: "Cockpit" },
  { to: "/board/risks", icon: AlertTriangle, label: "Risiken" },
  { to: "/board/compliance", icon: ShieldCheck, label: "Compliance / IKS" },
  { to: "/board/findings", icon: ClipboardCheck, label: "Audit-Feststellungen" },
  { to: "/board/meetings", icon: CalendarDays, label: "Sitzungen & Beschlüsse" },
  { to: "/board/documents", icon: FolderLock, label: "Dokumente" },
  { to: "/board/audit-trail", icon: History, label: "Audit-Trail" },
  { to: "/board/leads", icon: Inbox, label: "Leads" },
  { to: "/board/settings", icon: SettingsIcon, label: "Einstellungen" },
];

const BoardLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [company, setCompany] = useState(null);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newCo, setNewCo] = useState({ name: "", legal_form: "AG", sector: "", role: "", location: "" });

  const loadCompanies = async (selectId) => {
    const list = await boardFetch("/companies");
    setCompanies(list);
    const stored = selectId || localStorage.getItem("board_cid");
    const found = list.find((c) => c.id === stored) || list[0] || null;
    setCompany(found);
    if (found) localStorage.setItem("board_cid", found.id);
  };
  useEffect(() => { loadCompanies().catch((e) => toast.error(e.message)); }, []);

  const selectCompany = (c) => { setCompany(c); localStorage.setItem("board_cid", c.id); setSwitcherOpen(false); navigate("/board"); };

  const createCompany = async () => {
    if (!newCo.name.trim()) return toast.error("Name erforderlich");
    const c = await boardFetch("/companies", { method: "POST", json: newCo });
    setAddOpen(false); setNewCo({ name: "", legal_form: "AG", sector: "", role: "", location: "" });
    toast.success("Mandat angelegt");
    await loadCompanies(c.id);
  };

  return (
    <CompanyContext.Provider value={{ company, companies, reload: loadCompanies }}>
      <div className="flex min-h-screen bg-[#0a0a0a] text-white">
        {/* Sidebar */}
        <aside className="fixed inset-y-0 left-0 flex w-64 flex-col border-r border-white/10 bg-[#0d0d0d]">
          <div className="border-b border-white/10 px-6 py-6">
            <div className="flex items-center gap-2 text-champagne">
              <ShieldCheck size={18} strokeWidth={1.5} />
              <span className="text-[11px] uppercase tracking-[0.35em]">VR-Cockpit</span>
            </div>
            <img src="/mysymp-logo-gold.png" alt="MYSYMP" className="mt-3 h-6 w-auto" />
          </div>
          <nav className="flex-1 space-y-1 px-3 py-6">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${n.label}`}
                className={({ isActive }) => `flex items-center gap-3 rounded px-3 py-2.5 text-sm transition-colors ${isActive ? "bg-champagne/10 text-champagne" : "text-white/55 hover:bg-white/5 hover:text-white"}`}>
                <n.icon size={17} strokeWidth={1.6} /> {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="border-t border-white/10 px-4 py-4">
            <p className="truncate text-sm text-white/70">{user?.name}</p>
            <p className="truncate text-xs text-white/35">{user?.email}</p>
            <button onClick={logout} data-testid="board-logout" className="mt-3 flex items-center gap-2 text-xs uppercase tracking-widest text-white/45 hover:text-red-400">
              <LogOut size={14} /> Abmelden
            </button>
          </div>
        </aside>

        {/* Main */}
        <div className="ml-64 flex-1">
          <header className="sticky top-0 z-40 flex items-center justify-between border-b border-white/10 bg-[#0a0a0a]/90 px-8 py-4 backdrop-blur-xl">
            <div className="relative">
              <button onClick={() => setSwitcherOpen((o) => !o)} data-testid="mandate-switcher"
                className="flex items-center gap-3 border border-white/15 px-4 py-2.5 text-left hover:border-champagne/50">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-champagne">Mandat</p>
                  <p className="text-sm text-white">{company ? company.name : "—"}</p>
                </div>
                <ChevronDown size={16} className="text-white/40" />
              </button>
              {switcherOpen && (
                <div className="absolute left-0 top-full z-50 mt-2 w-72 border border-white/15 bg-[#141414] py-2 shadow-xl" data-testid="mandate-list">
                  {companies.map((c) => (
                    <button key={c.id} onClick={() => selectCompany(c)} data-testid={`mandate-option`}
                      className={`block w-full px-4 py-2.5 text-left text-sm hover:bg-white/5 ${company?.id === c.id ? "text-champagne" : "text-white/70"}`}>
                      {c.name}
                      <span className="block text-xs text-white/35">{c.role}</span>
                    </button>
                  ))}
                  <button onClick={() => { setSwitcherOpen(false); setAddOpen(true); }} data-testid="mandate-add"
                    className="mt-1 flex w-full items-center gap-2 border-t border-white/10 px-4 py-2.5 text-left text-sm text-champagne hover:bg-white/5">
                    <Plus size={14} /> Mandat hinzufügen
                  </button>
                </div>
              )}
            </div>
            {company && <span className="text-xs uppercase tracking-widest text-white/40">{company.role} · {company.location}</span>}
          </header>

          <main className="px-8 py-10">
            {company ? <Outlet /> : (
              <div className="py-20 text-center text-white/40">
                Kein Mandat vorhanden.{" "}
                <button onClick={() => setAddOpen(true)} className="text-champagne underline">Erstes Mandat anlegen</button>
              </div>
            )}
          </main>
        </div>

        {addOpen && (
          <Modal title="Neues Mandat" onClose={() => setAddOpen(false)}>
            <div className="space-y-4">
              {[
                ["name", "Firmenname"], ["legal_form", "Rechtsform"], ["sector", "Branche"],
                ["role", "Ihre Funktion (z. B. VR-Präsident)"], ["location", "Sitz"],
              ].map(([k, label]) => (
                <div key={k}>
                  <label className="mb-1.5 block text-xs uppercase tracking-wider text-white/50">{label}</label>
                  <input data-testid={`company-${k}`} value={newCo[k]} onChange={(e) => setNewCo((s) => ({ ...s, [k]: e.target.value }))}
                    className="w-full border border-white/15 bg-black/30 px-3 py-2.5 text-sm text-white outline-none focus:border-champagne" />
                </div>
              ))}
              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => setAddOpen(false)} className="border border-white/20 px-5 py-2.5 text-xs uppercase tracking-widest text-white/70">Abbrechen</button>
                <button onClick={createCompany} data-testid="company-save" className="bg-champagne px-5 py-2.5 text-xs uppercase tracking-widest text-obsidian">Anlegen</button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </CompanyContext.Provider>
  );
};

export default BoardLayout;
